import { IAccessLevel, type IPayload } from '@quesmed/types/models';
import {
  type ILoginAdminData,
  type ILoginAdminVar,
  type IRegisterAdminData,
  type IRegisterAdminVar,
  LOGIN_ADMIN,
  REGISTER_ADMIN,
} from '@quesmed/types/resolvers/mutation';
import { VALID_USER_TOKEN } from '@quesmed/types/resolvers/mutation/validUserToken';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config';
import { toaster } from '@/core/ui';
import { client, useMutation } from '@/libs';
import { authState } from '@/store';
import { ClientStorage, JWT } from '@/utils';

export const useAuthHandler = () => {
  const navigate = useNavigate();
  const setIsAuthPending = authState((s) => s.setIsAuthPending);
  const setIsAuthenticated = authState((s) => s.setIsAuthenticated);
  const setDemoUser = authState((s) => s.setDemoUser);
  const setToken = authState((s) => s.setToken);
  const reset = authState((s) => s.reset);
  const setIsSalesRole = authState((s) => s.setIsSalesRole);
  const setIsSuperAdmin = authState((s) => s.setIsSuperAdmin);
  const setTokenDecoded = authState((s) => s.setTokenDecoded);
  const setAccessLevel = authState((s) => s.setAccessLevel);

  const [loginUser, { loading: loginLoading }] = useMutation<ILoginAdminData, ILoginAdminVar>(LOGIN_ADMIN);
  const [registerUser, { loading: registerLoading }] = useMutation<IRegisterAdminData, IRegisterAdminVar>(
    REGISTER_ADMIN,
  );
  const [renewToken] = useMutation<{ validUserToken: { renewToken: string } }, {}>(VALID_USER_TOKEN);

  const login = async (variables: ILoginAdminVar) => {
    try {
      const { data } = await loginUser({
        variables,
      });
      if (data?.loginAdmin) {
        ClientStorage.cookies.setItem('ques-usertoken', data?.loginAdmin, {
          expires: variables.days * 24 * 60 * 60,
        });
        setIsAuthPending(false);
        setIsAuthenticated(true);
        setDemoUser(variables.username.includes('demo-'));
        setToken(data?.loginAdmin);
        getAuthInfo();
        navigate(ROUTES.MAIN);
      }
    } catch (_error) {
      navigate(ROUTES.LOGIN);
    }
  };

  const register = async (variables: IRegisterAdminVar) => {
    try {
      const { data } = await registerUser({ variables });
      if (data?.registerAdmin) {
        toaster.success('Registration successful! Please sign in.');
        navigate(ROUTES.LOGIN);
      }
    } catch (_error) {
      toaster.error('Registration failed. Please try again.');
    }
  };

  const logout = async () => {
    if (client) {
      await client.clearStore();
    }

    ClientStorage.cookies.clear();
    ClientStorage.local.clear();
    reset();
    navigate(ROUTES.LOGIN);
  };

  const refreshToken = async () => {
    try {
      const { data } = await renewToken();
      if (!data?.validUserToken?.renewToken) {
        throw new Error('Invalid Token');
      }
      const newToken = data.validUserToken.renewToken;
      ClientStorage.cookies.setItem('ques-usertoken', newToken, {
        expires: 7 * 24 * 60 * 60,
      });
      setIsAuthPending(false);
      setIsAuthenticated(true);
      setToken(newToken);
      const decoded = JWT.decode<IPayload>(newToken);
      setIsSuperAdmin(decoded.accessLevel === IAccessLevel.SUPER_ADMIN);
      setIsSalesRole(decoded.accessLevel === IAccessLevel.MARKETING);
      setAccessLevel(decoded.accessLevel);
      setDemoUser(decoded.username.includes('demo-'));
      setTokenDecoded(decoded);
    } catch (_error) {
      toaster.error('Token is Invalid, Please re-login to continue');
      navigate(ROUTES.LOGIN);
    }
  };

  const getAuthInfo = (): IPayload | null => {
    const token = ClientStorage.cookies.getItem<string>('ques-usertoken');
    if (!token) {
      ClientStorage.cookies.removeItem('ques-usertoken');
      setTokenDecoded(null);
      return null;
    }

    const decoded = JWT.decode<IPayload>(token);
    if (decoded.exp * 1000 <= Date.now()) {
      // refresh the token internally if expired
      refreshToken();
    }
    setIsSuperAdmin(decoded.accessLevel === IAccessLevel.SUPER_ADMIN);
    setIsSalesRole(decoded.accessLevel === IAccessLevel.MARKETING);
    setAccessLevel(decoded.accessLevel);
    setDemoUser(decoded.username.includes('demo-'));
    setTokenDecoded(decoded);
    return decoded;
  };

  return {
    getAuthInfo,
    login,
    logout,
    register,
    loginLoading,
    registerLoading,
  };
};
